'use client';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppSelector, useAppDispatch } from '@/store';
import { setFormPartial } from '@/store/property-create-slice';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import {
  propertyBasicInfoSchema,
  type PropertyBasicInfoData,
} from '@/schema/properties';
import { PropertyBasicInfo } from '@/components/properties/create/property-basic-info';
import { PropertyCreateProgress } from '@/components/properties/create/property-create-progress';

export default function PropertyCreateStep1Page() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRTL = i18n.language === 'ar';
  const dispatch = useAppDispatch();

  const persisted = useAppSelector(
    state => state.propertyCreate?.formData || {}
  );

  const handleCancel = () => navigate('/properties');

  const methods = useForm<PropertyBasicInfoData>({
    resolver: zodResolver(propertyBasicInfoSchema),
    defaultValues: persisted as PropertyBasicInfoData,
    mode: 'onTouched',
  });

  const { handleSubmit: rhfHandleSubmit } = methods;

  const onSubmit = async (data: PropertyBasicInfoData) => {
    try {
      // Save to Redux store
      dispatch(setFormPartial(data));

      // Navigate to step 2
      navigate('/properties/create/2');
    } catch (error) {
      console.error('Error saving step 1:', error);
      toast({
        title: t('properties.create.toasts.errorTitle'),
        description: t('properties.create.toasts.errorDesc'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div
            className={cn(
              'flex items-center gap-4',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className={cn('w-4 h-4', isRTL ? 'ml-2' : 'mr-2')} />
              {t('back')}
            </Button>
            <h1
              className={cn(
                'text-xl sm:text-2xl font-semibold',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('properties.create.title')}
            </h1>
            <Badge
              variant="secondary"
              className={cn(isRTL ? 'mr-auto' : 'ml-auto')}
            >
              {t('properties.create.badgeRole')}
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Progress Steps */}
        <PropertyCreateProgress currentStep={1} />

        <FormProvider {...methods}>
          <form onSubmit={rhfHandleSubmit(onSubmit)}>
            {/* Step Content */}
            <div className="space-y-6">
              <PropertyBasicInfo />
            </div>

            {/* Navigation Buttons */}
            <div
              className={cn(
                'flex gap-4 pt-8 border-t mt-8',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <Button type="submit" className="w-full sm:w-auto">
                {t('next')}
                <ArrowRight
                  className={cn('w-4 h-4', isRTL ? 'mr-2' : 'ml-2')}
                />
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                className="w-full sm:w-auto"
              >
                {t('cancel')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
